import type { MutableRefObject } from 'react'
import { useCallback } from 'react'

import { toError } from './async-state.helper'
import type { AsyncState } from './async-state.interface'

export interface UseAsyncExecuteOptions<T> {
  asyncFnRef: MutableRefObject<() => Promise<T>>
  isPendingRef: MutableRefObject<boolean>
  isMountedRef: MutableRefObject<boolean>
  setState: (state: AsyncState) => void
  setData: (data: T) => void
  setError: (error: Error | null) => void
}

export const useAsyncExecute = <T>({
  asyncFnRef,
  isPendingRef,
  isMountedRef,
  setState,
  setData,
  setError,
}: UseAsyncExecuteOptions<T>): (() => void) => {
  return useCallback(() => {
    if (isPendingRef.current) return

    isPendingRef.current = true
    setState('pending')
    setError(null)

    asyncFnRef
      .current()
      .then((result) => {
        if (isMountedRef.current) {
          setData(result)
          setState('done')
        }
      })
      .catch((err) => {
        if (isMountedRef.current) {
          setError(toError(err))
          setState('error')
        }
      })
      .finally(() => {
        isPendingRef.current = false
      })
  }, [asyncFnRef, isPendingRef, isMountedRef, setState, setData, setError])
}
